import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserCheck, CheckCheck } from "lucide-react";
import { AbsenceAlertsCard } from "@/components/AbsenceAlertsCard";

const today = () => new Date().toLocaleDateString("en-CA");
const OPTIONS = [["present", "Present", "bg-emerald-600 text-white"], ["late", "Late", "bg-sky-600 text-white"], ["half_day", "Half day", "bg-amber-500 text-white"], ["absent", "Absent", "bg-rose-600 text-white"], ["leave", "Leave", "bg-violet-600 text-white"]];

function StatusPicker({ id, value, onChange }) {
  return (
    <div className="flex gap-1.5">
      {OPTIONS.map(([k, l, on]) => (
        <button key={k} type="button" data-testid={`staff-att-${id}-${k}`} onClick={() => onChange(k)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${value === k ? on : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function StaffAttendancePage() {
  const [day, setDay] = useState(today());
  const [rows, setRows] = useState([]);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/hr/staff-attendance", { params: { date: day } }).then((r) => {
    setRows(r.data);
    setMarks(Object.fromEntries(r.data.filter((x) => x.status).map((x) => [x.id, x.status])));
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

  const allPresent = () => setMarks(Object.fromEntries(rows.filter((x) => !x.on_leave).map((x) => [x.id, "present"]).concat(Object.entries(marks).filter(([id]) => rows.find((r) => r.id === id)?.on_leave))));
  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/hr/staff-attendance", { date: day, entries: Object.entries(marks).map(([user_id, status]) => ({ user_id, status })) });
      toast.success(`Attendance saved for ${data.saved} staff`); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  }
  const count = (s) => Object.values(marks).filter((v) => v === s).length;

  return (
    <AppShell>
      <PageHeader eyebrow="HR" title="Staff Attendance" subtitle="Mark staff present, late, half day or absent each day. Absent and half days are deducted in monthly payroll (approved leave days are not). Every 3 late marks in a month = half day's pay cut." />
      <AbsenceAlertsCard refreshKey={rows} />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><Label className="text-xs">Date</Label><Input data-testid="staff-att-date" type="date" max={today()} value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 w-44 bg-white" /></div>
        <Button data-testid="staff-att-all-present" variant="outline" onClick={allPresent}><CheckCheck size={15} className="mr-1" /> Mark all present</Button>
        <div data-testid="staff-att-summary" className="text-sm">Present <b className="text-emerald-700">{count("present")}</b> • Late <b className="text-sky-700">{count("late")}</b> • Half day <b className="text-amber-700">{count("half_day")}</b> • Absent <b className="text-rose-700">{count("absent")}</b> • Unmarked <b>{rows.length - Object.keys(marks).length}</b></div>
        <Button data-testid="staff-att-save" onClick={save} disabled={saving || !Object.keys(marks).length} className="ml-auto btn-brand">{saving ? "Saving…" : "Save attendance"}</Button>
      </div>
      {rows.length === 0 ? <Empty testid="staff-att-empty" icon={UserCheck} text="No staff found." /> : (
        <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
          {rows.map((s) => (
            <div key={s.id} data-testid={`staff-att-row-${s.id}`} className="px-4 py-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[160px]">
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs text-slate-500">{ROLE_LABELS[s.role]}{s.marked_by ? ` • marked by ${s.marked_by}` : ""}</div>
              </div>
              {s.on_leave && <Badge data-testid={`staff-att-leave-${s.id}`} className="bg-violet-100 text-violet-800">On approved leave</Badge>}
              <StatusPicker id={s.id} value={marks[s.id]} onChange={(v) => setMarks({ ...marks, [s.id]: v })} />
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
